import React from 'react';
import './TodoItem.css';
import Checkbox from './Checkbox';
import MenuButton from './MenuButton';
import Icon from './icons/Icon';
import { isShoppingHabitId } from '../utils/habits';
import { getLocalDateString } from '../data/todoData';
import { getTokyoDateParts } from '../utils/dateUtils';

interface TodoItemProps {
  id: string;
  title: string;
  body?: string;
  startAt: string;
  done: boolean;
  isExpired: boolean;
  doneAt?: string;
  habitId?: string;
  onItemClick?: () => void;
  onToggleComplete?: (id: string) => void;
  onDelete?: (id: string) => void;
  isSelected?: boolean;
  disabled?: boolean;
  // タグ・習慣タブでは日付も表示する
  showDateInTime?: boolean;
}


const TodoItem: React.FC<TodoItemProps> = ({
  id,
  title,
  body,
  startAt,
  done,
  isExpired,
  doneAt,
  habitId,
  onItemClick,
  onToggleComplete,
  onDelete,
  isSelected = false,
  disabled = false,
  showDateInTime = false,
}) => {

  // 期限切れの場合の超過時間を計算（仕様書に基づく）
  const getOverdueStatus = (): string | null => {
    if (done || !isExpired) return null;
    
    const now = new Date();
    const dueDateTime = new Date(startAt);
    const diffMs = now.getTime() - dueDateTime.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) {
      // 1時間以内なら〇〇分超過
      return `${diffMinutes}分超過`;
    } else if (diffHours < 24) {
      // 24時間以内なら〇〇時間超過
      return `${diffHours}時間超過`;
    } else {
      // それ以上なら〇〇日超過（純粋な日付の差で計算）
      const currentDateString = getLocalDateString(new Date().toISOString());
      const todoDateString = getLocalDateString(startAt);
      
      const currentDate = new Date(currentDateString);
      const todoDate = new Date(todoDateString);
      
      // 日付の差を計算（時刻は無視）
      const diffTime = currentDate.getTime() - todoDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      return `${diffDays}日超過`;
    }
  };

  // 完了日時を（YYYY年M月D日 HH:MM 完了）形式でフォーマット
  const getCompletedDateDisplay = (): string | null => {
    if (!done || !doneAt) return null;
    
    const { year, month, day } = getTokyoDateParts(doneAt);
    
    return `（完了 : ${year}年${month}月${day}日）`;
  };

  // CheckboxクリックをTodoItemクリックから分離
  const handleCheckboxClick = () => {
    if (!disabled) {
      onToggleComplete?.(id);
    }
  };

  // disabled時のCheckboxクリックハンドラー
  const handleDisabledCheckboxClick = () => {
    if (disabled) {
      // disabled時（drawer開いている時）はTodo詳細を表示
      onItemClick?.();
    }
  };

  // TodoItemクリック（詳細ダイアログ表示）
  const handleItemClick = () => {
    onItemClick?.();
  };

  // キーボードイベント（アクセシビリティ対応）
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onItemClick?.();
    }
  };

  const getTodoState = (): 'default' | 'expired' => {
    return isExpired ? 'expired' : 'default';
  };

  const getClassName = () => {
    const baseClass = 'todo-item';
    const stateClass = `todo-item--${getTodoState()}`;
    const completedClass = done ? 'todo-item--completed' : '';
    const selectedClass = isSelected ? 'todo-item--selected' : '';
    
    return [baseClass, stateClass, completedClass, selectedClass].filter(Boolean).join(' ');
  };

  const overdueStatus = getOverdueStatus();
  const completedDateDisplay = getCompletedDateDisplay();

  // startAt の表示切り替え
  const getStartAtDisplay = (): string => {
    const { month, day, hour, minute } = getTokyoDateParts(startAt);
    if (!showDateInTime) {
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }
    const mm = month.toString().padStart(2, '0');
    const dd = day.toString().padStart(2, '0');
    const hh = hour.toString().padStart(2, '0');
    const mi = minute.toString().padStart(2, '0');
    return `${mm}月${dd}日 ${hh}:${mi}`;
  };

  return (
    <div 
      className={getClassName()}
      onClick={handleItemClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Todo項目: ${title}`}
    >
      <div className="todo-item__variable">
        <div className="todo-item__state">
          <div
            className="todo-item__checkbox"
            // チェックボックス操作時に親の onClick/onKeyDown へイベントがバブリングして
            // ドロワーが開いてしまうのを防ぐ
            onMouseDown={(e) => e.stopPropagation()} // マウス押下段階で止める（クリック/フォーカス移動の両方に有効）
            onClick={(e) => e.stopPropagation()}     // 念のため click でも止める
            onKeyDown={(e) => {
              // スペース/Enter は親のキーボードショートカット（ドロワーを開く）に使われるため止める
              if (e.key === ' ' || e.key === 'Enter') {
                e.stopPropagation();
              }
            }}
          >
            <Checkbox
              checked={done}
              onChange={handleCheckboxClick} // Checkboxから直接呼び出す
              disabled={disabled}
              onDisabledClick={handleDisabledCheckboxClick}
            />
          </div>
          <div className="todo-item__text">
            <div className={`todo-item__title ${habitId ? 'todo-item__title--with-habit-icon' : ''}`}>
              {habitId && (
                <div className="todo-item__habit-icon">
                  <Icon name={isShoppingHabitId(habitId) ? 'shop' : 'habit'} size={16} />
                </div>
              )}
              <span className="todo-item__title-text text-single-xlarge">{title}</span>
            </div>
            {body && (
              <div className="todo-item__description text-body-medium">
                {body}
              </div>
            )}
            <div className="todo-item__time">
              {/* startAtから時間を抽出して表示 */}
              <span className="todo-item__time-display text-body-small">
                {getStartAtDisplay()}
              </span>
              
              {/* 補足情報：完了済みの場合は完了日時、未完了の場合は超過状況 */}
              {done ? (
                completedDateDisplay && (
                  <span className="todo-item__time-assistive text-body-small">
                    {completedDateDisplay}
                  </span>
                )
              ) : (
                overdueStatus && (
                  <span className="todo-item__time-assistive text-body-small">
                    （{overdueStatus}）
                  </span>
                )
              )}
            </div>
          </div>
          <MenuButton
            icon="menu"
            tooltip="Todo 操作メニュー"
            tooltipPosition="bottom-left"
            aria-label="Todo 操作メニュー"
            items={[
              {
                type: 'Destructive',
                label: '削除する',
                leftIcon: 'delete',
                showLeftIcon: true,
                onClick: () => {
                  if (id !== '') {
                    onDelete?.(id);
                  }
                }
              }
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default TodoItem;
